import { observer } from 'mobx-react-lite'
import { type NotificationListViewModel } from '@/features/notifications/NotificationListViewModel'
import { useActivation } from '@/hooks/view-model-hooks'
import { type NotificationItemViewModel } from '@/features/notifications/NotificationItemViewModel'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { useFeatures } from '@/contexts/root-store-context'
import {
  NotificationGroupView,
  NotificationItem,
  NotificationList,
  NotificationListEmpty,
  NotificationListError,
} from '@/features/notifications/components/NotificationList'

export interface NotificationListContainerProps {
  vm: NotificationListViewModel
}

export const NotificationListContainer = observer(
  function NotificationListContainer({ vm }: NotificationListContainerProps) {
    useActivation(vm)

    return (
      <NotificationList
        loader={
          vm.isRevalidating ? null : (
            <InfiniteScrollLoaderContainer scrollProvider={vm.scroller} />
          )
        }
      >
        {vm.fetch.match({
          pending: () =>
            vm.isRevalidating ? <NotificationGroupsContainer vm={vm} /> : null,
          rejected: (err) => <NotificationListError error={err} />,
          resolved: () => <NotificationGroupsContainer vm={vm} />,
        })}
      </NotificationList>
    )
  },
)

const NotificationGroupsContainer = observer(
  function NotificationGroupsContainer({ vm }: NotificationListContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))

    if (vm.notificationGroups.length === 0) {
      return <NotificationListEmpty corporateMode={shellInfo.corporateMode} />
    }

    return vm.notificationGroups.map((group, i) => (
      <NotificationGroupView
        key={i}
        isOlder={group.type === 'Older'}
        showMarkAllAsRead={i === 0}
        isMarkAllAsReadPending={vm.markAllAsRead.pending}
        onMarkAllAsRead={vm.markAllAsRead}
      >
        {group.notifications.map((item) => (
          <NotificationItemContainer key={item.id} vm={item} />
        ))}
      </NotificationGroupView>
    ))
  },
)

export interface NotificationItemContainerProps {
  vm: NotificationItemViewModel
}

export const NotificationItemContainer = observer(
  function NotificationItemContainer({ vm }: NotificationItemContainerProps) {
    return (
      <NotificationItem
        actionUrl={vm.actionUrl}
        onPress={vm.markAsRead}
        imageSource={vm.imageSource}
        text={vm.text}
        plainText={vm.plainText}
        dispatchedAt={vm.dispatchedAt}
        workspaceName={vm.workspaceName}
        isRead={vm.isRead}
      />
    )
  },
)
